import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { Loader } from '@/components/Loader';
import { MarkdownEditor } from '@/components/markdown/MarkdownEditor';
import { NoData } from '@/components/ui/NoData';
import { useDownloadFile, useOpenFile } from '@/hooks/useFiles';
import { ParsedFileTypes } from '@/types';
import { detectBinaryFile, getFileType } from '@/utils/file.util';

const FileViewer = ({
  fileName,
  onChange,
  onClose,
  email,
}: {
  fileName: string;
  onChange?: (value: string) => void;
  onClose: () => void;
  email?: string;
}) => {
  const { mutate: downloadFile } = useDownloadFile(email);
  const { mutate: getFileContent, data: fileContent } = useOpenFile(email);
  const [editorContent, setEditorContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [isBinary, setIsBinary] = useState(false);
  const [isFileTooLarge, setIsFileTooLarge] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fileType = fileName ? getFileType(fileName) : '';

  useEffect(() => {
    return () => {
      setEditorContent('');
      setImageUrl('');
      setIsBinary(false);
      setIsFileTooLarge(false);
      setIsLoading(true);
    };
  }, []);

  useEffect(() => {
    if (fileName) {
      setIsLoading(true);
      setIsFileTooLarge(false);

      // Set 3 second timeout
      const timeoutId = setTimeout(() => {
        setIsFileTooLarge(true);
        setIsLoading(false);
      }, 3000);

      getFileContent({ name: fileName });

      return () => clearTimeout(timeoutId);
    }
  }, [fileName, getFileContent]);

  useEffect(() => {
    const processFileContent = async () => {
      if (!fileContent) return;

      // File loaded successfully, clear timeout flag and loading state
      setIsFileTooLarge(false);
      setIsLoading(false);

      if (fileType === ParsedFileTypes.IMAGE) {
        setImageUrl(URL.createObjectURL(fileContent));
      } else if (fileType === ParsedFileTypes.PDF) {
        const blob = new Blob([fileContent], { type: 'application/pdf' });
        const blobURL = URL.createObjectURL(blob);
        window.open(blobURL);
        onClose();
      } else if (fileType === ParsedFileTypes.BINARY) {
        // Known binary extension, skip content detection
        setIsBinary(true);
      } else {
        // For DOCUMENT type, detect if content is actually binary
        const isBinaryContent = await detectBinaryFile(fileContent, fileName);

        if (isBinaryContent) {
          setIsBinary(true);
        } else {
          // Handling text files, converting blob to text
          const reader = new FileReader();
          reader.onload = function (event) {
            if (event.target) {
              const content = event.target.result as string;
              setEditorContent(content);
              // Notify parent component of the initial content
              onChange && onChange(content);
            }
          };
          reader.readAsText(fileContent);
        }
      }
    };

    processFileContent();
  }, [fileContent, fileType, fileName, onChange, onClose]);
  // Rest of the code...

  // Conditional rendering based on content type
  if (isLoading && !fileContent) {
    return <Loader isLoading />;
  }

  // Handle files that are too large to display (timeout triggered)
  if (isFileTooLarge) {
    return (
      <NoData
        title="This file is too large to be displayed"
        action={
          <div className="bg-chariot-background-secondary border-chariot-stroke inline-flex rounded-lg border p-4">
            <Button
              label="Download File"
              startIcon={<ArrowDownTrayIcon className="size-5" />}
              onClick={() => {
                downloadFile({ name: fileName });
              }}
            />
          </div>
        }
      />
    );
  }

  // Handle binary files with message and download button
  if (isBinary) {
    return (
      <NoData
        title="This file type cannot be displayed"
        action={
          <div className="bg-chariot-background-secondary border-chariot-stroke inline-flex rounded-lg border p-4">
            <Button
              label="Download File"
              startIcon={<ArrowDownTrayIcon className="size-5" />}
              onClick={() => {
                downloadFile({ name: fileName });
              }}
            />
          </div>
        }
      />
    );
  }

  if (fileType === ParsedFileTypes.IMAGE) {
    return <img src={imageUrl} alt="File content" />;
  } else if (fileType === ParsedFileTypes.DOCUMENT) {
    return (
      <div className="h-[60vh]">
        <MarkdownEditor
          value={editorContent}
          onChange={value => {
            setEditorContent(value ?? '');
            onChange && onChange(value ?? '');
          }}
          filePathPrefix="proof-of-exploit/files"
        />
      </div>
    );
  }

  return null;
};

export default FileViewer;
